import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import InputDateAndTime from '@components/InputDateAndTime/InputDateAndTime.tsx';
import dayjs from 'dayjs';
import { InputNumber } from '@components/framework/input/InputNumber';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';

export type DeadlineMode = 'relative' | 'absolute';

export type DeadlineUnit = 'hours' | 'days';

interface ComponentProps {
  mode: DeadlineMode;
  offsetValue?: number;
  offsetUnit?: DeadlineUnit;
  absoluteLocalTime?: dayjs.Dayjs;
  absoluteTimezone?: string;
  onChangeRelative: (value: number, unit: DeadlineUnit) => void;
  onChangeAbsolute: (localDate: dayjs.Dayjs, timezoneId: string) => void;
  onModeChange: (mode: DeadlineMode) => void;
  dataTestIdCalendar?: string;
  dataTestIdTimezone?: string;
  value?: any;
  preserveAbsoluteOnFirstFlip?: boolean;
}

const InputDeadline: React.FC<ComponentProps> = ({
                                                   mode,
                                                   offsetValue,
                                                   offsetUnit = 'hours',
                                                   absoluteLocalTime,
                                                   absoluteTimezone,
                                                   onChangeRelative,
                                                   onChangeAbsolute,
                                                   onModeChange,
                                                   dataTestIdCalendar,
                                                   dataTestIdTimezone,
                                                   preserveAbsoluteOnFirstFlip = false,
                                                 }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const unitOptions = [
    { value: 'hours' as DeadlineUnit, label: tr('InputDeadline.hours', 'hours') },
    { value: 'days' as DeadlineUnit, label: tr('InputDeadline.days', 'days') },
  ];

  const timeZone = absoluteTimezone || loggedUser.defaultTimezoneId || 'UTC';

  const [relativeTouched, setRelativeTouched] = useState(false);
  const handleChangeRelative = (value: number, unit: DeadlineUnit) => {
    setRelativeTouched(true);
    onChangeRelative(value, unit);
  };

  if (mode === 'relative') {
    return (
      <div className="flex flex-col gap-2 w-full">
        <div className="grid min-h-[40px] w-full" style={{ gridTemplateColumns: '1fr auto auto', columnGap: '4px' }}>
          <InputNumber
            value={offsetValue}
            onValueChange={e => handleChangeRelative(e.value ?? 0, offsetUnit)}
            min={1}
            showButtons={false}
            className="w-full"
            inputClassName="w-full h-[40px]"
          />
          <div className="flex flex-col gap-px h-[40px]">
            <RingilButton
              mode="filled-rectangle"
              size="small"
              icon={<i className="pi pi-chevron-up" style={{ fontSize: '0.6rem' }} />}
              onClick={() => handleChangeRelative((offsetValue ?? 0) + 1, offsetUnit)}
              className="!py-0 !px-2 !rounded-b-none flex-1"
            />
            <RingilButton
              mode="filled-rectangle"
              size="small"
              icon={<i className="pi pi-chevron-down" style={{ fontSize: '0.6rem' }} />}
              onClick={() => handleChangeRelative(Math.max(1, (offsetValue ?? 1) - 1), offsetUnit)}
              className="!py-0 !px-2 !rounded-t-none flex-1"
            />
          </div>
          <Dropdown
            value={offsetUnit}
            options={unitOptions}
            optionValue="value"
            optionLabel="label"
            onChange={e => handleChangeRelative(offsetValue ?? 1, e.value)}
            className="w-32 ml-1 h-[40px]"
          />
        </div>
        <div
          className="text-content-brand cursor-pointer text-sm transition-all duration-200 hover:text-complementary-brandDark"
          onClick={() => {
            if (preserveAbsoluteOnFirstFlip && !relativeTouched) {
              onModeChange('absolute');
              return;
            }
            const offsetHours = offsetUnit === 'days' ? (offsetValue ?? 1) * 24 : (offsetValue ?? 1);
            onChangeAbsolute(dayjs().add(offsetHours, 'hours'), timeZone);
          }}
          data-testid="carrierSelection-deadline-setExactDateTime"
        >
          {tr('InputDeadline.setExactDateTime', 'Set exact date and time')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <InputDateAndTime
        value={absoluteLocalTime}
        onChange={(localDate, timezoneId) => {
          onChangeAbsolute(localDate as dayjs.Dayjs, timezoneId);
        }}
        timeZone={timeZone}
        fullWidth
        quickPrefills={[
          { value: 8, unit: 'hour', label: tr('InputDeadline.plus8Hours', '+8 hours') },
          { value: 1, unit: 'day', label: tr('InputDeadline.plus1Day', '+1 day') },
          { value: 3, unit: 'day', label: tr('InputDeadline.plus3Days', '+3 days') },
        ]}
        dataTestIdCalendar={dataTestIdCalendar}
        dataTestIdTimezone={dataTestIdTimezone}
      />
      <div
        className="text-content-brand cursor-pointer text-sm transition-all duration-200 hover:text-complementary-brandDark"
        onClick={() => onModeChange('relative')}
      >
        {tr('InputDeadline.setRelativeDeadline', 'Set relative deadline')}
      </div>
    </div>
  );
};

export default InputDeadline;
